/**
 * Shape helpers for the builder's internal model (the `screens` array).
 *
 * Moved verbatim out of `hooks/useFlowBuilder.js`, these are pure structural
 * operations with no React in them, shared by the hook (editing) and by
 * `hydrate.js` (loading a flow).
 */

import { generateId } from './idGeneration';

export const defaultRouting = () => ({
  mode: 'simple',
  rules: [],
  defaultNextScreenId: null,
});

// Keys under which a container component holds an immediate list of child components.
// If uses then/else; some containers use a generic `children` list; Switch holds its
// children one level deeper, inside each entry of `cases` (cases[*].children).
export const BRANCH_KEYS = ['then', 'else', 'children'];

// Run `fn(childArray)` for every branch child-list a component's properties hold,
// including each Switch case's children. The arrays are passed live, so callers may
// mutate them in place (used by the deep-clone id regenerator).
export function forEachBranchList(properties, fn) {
  if (!properties) return;
  for (const key of BRANCH_KEYS) {
    if (Array.isArray(properties[key])) fn(properties[key]);
  }
  if (Array.isArray(properties.cases)) {
    for (const branch of properties.cases) {
      if (Array.isArray(branch.children)) fn(branch.children);
    }
  }
}

// Immutably map every branch child-list (If then/else, generic children, and every
// Switch case's children) through `mapChildren`. Returns the SAME properties object
// when nothing changed, so callers can skip needless component re-creation/re-renders.
export function mapBranchLists(properties, mapChildren) {
  if (!properties) return properties;
  let changed = false;
  const next = { ...properties };
  for (const key of BRANCH_KEYS) {
    if (Array.isArray(properties[key])) {
      const mapped = mapChildren(properties[key]);
      if (mapped !== properties[key]) {
        next[key] = mapped;
        changed = true;
      }
    }
  }
  if (Array.isArray(properties.cases)) {
    let casesChanged = false;
    const cases = properties.cases.map(branch => {
      if (Array.isArray(branch.children)) {
        const mapped = mapChildren(branch.children);
        if (mapped !== branch.children) {
          casesChanged = true;
          return { ...branch, children: mapped };
        }
      }
      return branch;
    });
    if (casesChanged) {
      next.cases = cases;
      changed = true;
    }
  }
  return changed ? next : properties;
}

export function ensureBranchIds(props) {
  return mapBranchLists(props, list => list.map(item => ({ ...item, id: item.id || generateId() })));
}

// Find a component by id within a screen, including inside If/Switch branches. Used to resolve the
// selected component live from current state instead of a possibly-stale stored reference.
export function findComponentInScreenById(screen, id) {
  if (!screen || !id) {
    return null;
  }
  for (const comp of screen.components) {
    if (comp.id === id) {
      return comp;
    }
    let found = null;
    forEachBranchList(comp.properties, list => {
      if (!found) {
        const match = list.find(sub => sub.id === id);
        if (match) found = match;
      }
    });
    if (found) {
      return found;
    }
  }
  return null;
}

// Helper function for array reordering
export const arrayMove = (array, fromIndex, toIndex) => {
  const element = array[fromIndex];
  const newArray = [...array];
  newArray.splice(fromIndex, 1);
  newArray.splice(toIndex, 0, element);
  return newArray;
};
